import { useState, useEffect, useRef } from 'react';
import Autoplay from 'embla-carousel-autoplay';
import { getBannersByPosition, IAdSpacePosition, type Banner } from './utils';
import { Carousel, CarouselApi, CarouselContent, CarouselItem } from '../../../../components/Common/UI/Carousel';
import { cn } from '../../../../utils';

interface IProps {
  position: IAdSpacePosition;
}

function Banner({ position }: IProps) {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const [api, setApi] = useState<CarouselApi | null>(null);

  const autoplay = useRef(
    Autoplay({
      delay: 5000,
      stopOnInteraction: false,
    })
  );

  useEffect(() => {
    setBanners(getBannersByPosition(position));
  }, []);

  useEffect(() => {
    if (!api) return undefined;

    const onSelect = () => {
      setSelectedIndex(api.selectedScrollSnap());
    };

    api.on('select', onSelect);
    onSelect();

    return () => {
      api.off('select', onSelect);
    };
  }, [api]);

  if (banners.length === 0) return null;

  return (
    <div
      className={cn('w-full relative overflow-hidden mb-5', {
        'px-5': position === 'headerSpace',
      })}
    >
      <Carousel
        plugins={[autoplay.current]}
        opts={{ loop: true }}
        setApi={setApi}
        className="w-full rounded-[6px] relative overflow-hidden"
      >
        <CarouselContent>
          {banners?.map(banner => (
            <CarouselItem key={banner.id}>
              <a
                href={banner.url}
                target="_blank"
                className={cn('block relative w-full rounded-[6px] overflow-hidden', {
                  'max-h-[350px]': position === 'headerSpace',
                  'max-h-[250px]': position === 'desktopSide',
                  'max-h-[200px]': position === 'mobileSpace',
                })}
              >
                <img
                  src={banner.imgData}
                  alt={banner.name}
                  className={cn('block w-full h-auto object-contain', {
                    'min-[1650px]:object-cover max-h-[350px]': position === 'headerSpace',
                    'max-h-[250px]': position === 'desktopSide',
                    'max-h-[200px]': position === 'mobileSpace',
                  })}
                />
                <div className="relative z-10 flex flex-col justify-center p-6 md:p-10 max-w-xl hidden">
                  <h3 className="text-xl font-semibold text-white">{banner.name}</h3>
                  {banner.details && <p className="text-white/90 mt-2">{banner.details}</p>}
                </div>
              </a>
            </CarouselItem>
          ))}
        </CarouselContent>
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20">
          {banners?.length > 1
            ? banners?.map((banner, index) => (
                <button
                  key={banner.id}
                  type="button"
                  aria-label={`Go to slide ${index + 1}`}
                  onClick={() => {
                    api?.scrollTo(index);
                    setSelectedIndex(index);
                  }}
                  className={cn('w-2 h-2 rounded-full transition-colors bg-white/50', {
                    'bg-white': index === selectedIndex,
                    'hover:bg-white/70': index !== selectedIndex,
                  })}
                />
              ))
            : null}
        </div>
      </Carousel>
    </div>
  );
}

export default Banner;
